import { makeZip } from 'client-zip';
import { DebouncedFunc, throttle } from 'lodash';
import mimeDB from 'mime-db';

import {
  DOWNLOAD_BATCH_SIZE,
  FETCH_MAX_RETRIES,
  FETCH_RETRY_DELAY_MS,
  HEAD_REQUEST_CONCURRENCY,
  MAX_DOWNLOAD_SIZE_BYTES,
} from '@/consts/download.consts';
import { createScopedLogger, log } from '@/logger/logger';
import { mapConcurrentGenerator } from '@/utils/async.utils';
import { getFileExtension } from '@/utils/urls';
import type { ScopedLogger } from '@/logger/logger';

import type { FileEntry, FolderEntry } from '@/types/downloadable';

export class DownloadSizeExceededError extends Error {
  constructor(public readonly sizeInBytes: number) {
    super(`Download size ${sizeInBytes} Bytes exceeds maximum allowed ${MAX_DOWNLOAD_SIZE_BYTES} Bytes`);
    this.name = 'DownloadSizeExceededError';
  }
}

const logger = log.getLogger('BrowserDownloader');

interface FileWithPathInZip {
  path: string;
  url: string;
  sizeBytes?: number;
}

type SingleFileFetchResult =
  | { success: true; path: string; url: string; blob: Blob }
  | {
      success: false;
      path: string;
      url: string;
      errorMessage: string;
      errorName: string;
    };

type ZipStreamEntry = { name: string; input: Blob };

/**
 * Public progress for download operations. Only one phase: preparing.
 * totalBytes is sent once on the first call so the UI can run the size threshold check.
 */
export type DownloadProgress = {
  phase: 'preparing';
  percent: number;
  totalBytes?: number;
};

export type DownloadProgressCallback = (progress: DownloadProgress) => void;

// Utility function to get MIME type from extension
function getMimeType(extension: string): string {
  const db = mimeDB as Record<string, { extensions?: string[] }>;
  for (const mimeType in db) {
    const info = db[mimeType];
    if (info?.extensions && info.extensions.includes(extension)) {
      return mimeType;
    }
  }
  return 'application/octet-stream';
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException('Download cancelled', 'AbortError');
  }
}

const PROGRESS_REPORTS_THROTTLE_MS = 200;
/**
 * BrowserDownloader - Infrastructure service for downloading files to user's computer
 *
 * Responsibilities:
 * - Download single files or folder structures (as ZIP)
 * - Handle File System Access API with automatic fallback
 * - Fetch remote files and convert to blobs
 * - No knowledge of application business logic
 */
export class BrowserDownloader {
  private readonly preferFilePicker: boolean;

  constructor({ preferFilePicker = true }: { preferFilePicker?: boolean } = {}) {
    this.preferFilePicker = preferFilePicker;
  }

  /**
   * Opens the OS "Save As…" picker. MUST be invoked synchronously from a user-gesture handler
   * (no awaits between the click and this call) — Chromium requires transient user activation
   * for showSaveFilePicker, and slow upstream awaits (e.g. server re-encode) consume it. Returns
   * the chosen handle, or null if the user cancelled. Caller must check `'showSaveFilePicker' in
   * window` first; throws otherwise.
   */
  async openSavePicker(opts: {
    suggestedName: string;
    fileType: string;
    mimeType: string;
  }): Promise<FileSystemFileHandle | null> {
    const { suggestedName, fileType, mimeType } = opts;
    const lastDot = suggestedName.lastIndexOf('.');
    const extension = lastDot >= 0 ? suggestedName.slice(lastDot) : '';
    try {
      return await window.showSaveFilePicker({
        suggestedName,
        types: [
          {
            description: fileType,
            // @ts-expect-error - File System Access API types are too restrictive for dynamic mime types
            accept: mimeType && extension ? { [mimeType]: [extension] } : undefined,
          },
        ],
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Writes a blob to a previously-opened FileSystemFileHandle. Defers `createWritable()` until
   * here so the file at the user's chosen path stays untouched if blob generation upstream fails.
   */
  async writeBlobToHandle(handle: FileSystemFileHandle, blob: Blob): Promise<void> {
    const writable = await handle.createWritable();
    try {
      await writable.write(blob);
      await writable.close();
    } catch (error) {
      // Discard the in-memory swap file rather than committing a partial write to the user's
      // chosen path. If abort itself fails (e.g. stream already aborted), the original error
      // is still what we want to surface.
      await writable.abort().catch(() => {});
      throw error;
    }
  }

  /**
   * Streams a response body to a previously-opened FileSystemFileHandle without holding it in
   * memory. On failure `pipeTo` aborts the writable, discarding the swap file like
   * `writeBlobToHandle`.
   */
  async writeStreamToHandle(handle: FileSystemFileHandle, stream: ReadableStream<Uint8Array>): Promise<void> {
    await stream.pipeTo(await handle.createWritable());
  }

  /**
   * Downloads a single file from a URL
   */
  async downloadFile(fileUrl: string, fileName: string, fileType: string, signal?: AbortSignal): Promise<void> {
    const correlationId = crypto.randomUUID();
    const scopedLog = createScopedLogger(logger, { correlationId });
    scopedLog.info('Download started', { fileName });

    const getFile = async () => {
      const response = await fetch(fileUrl, { signal });
      if (!response.ok) throw new Error(`Failed to fetch file: ${response.statusText}`);
      return response.blob();
    };

    const fileExtension = getFileExtension(fileUrl, fileType);
    await this.saveToDisk({
      getBlob: getFile,
      fileName,
      fileType: 'File',
      mimeType: getMimeType(fileExtension.toLowerCase()),
      scopedLog,
      signal,
    });
  }

  /**
   * Downloads a blob directly
   */
  async downloadBlob(blob: Blob, fileName: string, mimeType: string, fileType = 'File'): Promise<void> {
    const correlationId = crypto.randomUUID();
    const scopedLog = createScopedLogger(logger, { correlationId });
    scopedLog.info('Download started', { fileName });

    const getBlob = () => Promise.resolve(blob);

    await this.saveToDisk({
      getBlob,
      fileName,
      fileType,
      mimeType,
      scopedLog,
    });
  }

  /**
   * Downloads a folder structure as a ZIP file
   */
  async downloadFolder(
    rootFolder: FolderEntry,
    onProgress?: DownloadProgressCallback,
    signal?: AbortSignal,
  ): Promise<void> {
    const correlationId = crypto.randomUUID();
    const scopedLog = createScopedLogger(logger, { correlationId });

    const createZip = async () => {
      const downloadStartTime = performance.now();
      // 1. Collect all files from folder structure
      const allFiles = this.collectFilesFromFolder(rootFolder);
      scopedLog.info('Folder download started', {
        folderName: rootFolder.name,
        totalFiles: allFiles.length,
      });

      // 2. Calculate total size with capped HEAD-request concurrency.
      // Logging the number of HEAD failures here can reveal whether CDN rate limiting
      // is still occurring after this fix (it would show up as a high headFailures count
      // in the getTotalSize log entry).
      const totalBytes = await this.getTotalSize(allFiles, scopedLog, signal);
      scopedLog.info('Size calculation done', {
        totalFiles: allFiles.length,
        totalBytes: totalBytes ?? undefined,
        elapsedMs: (performance.now() - downloadStartTime).toFixed(0),
      });
      if (totalBytes && totalBytes > MAX_DOWNLOAD_SIZE_BYTES) {
        throw new DownloadSizeExceededError(totalBytes);
      }
      if (totalBytes) {
        scopedLog.debug(`Total download size: ${(totalBytes / 1024 / 1024).toFixed(2)} MB`);
      }

      // 3. Download files and stream into ZIP
      return await this.createZipBlobFromFiles(allFiles, totalBytes, scopedLog, onProgress, signal);
    };

    const zipFileName = `${rootFolder.name}.zip`;

    // 5. Save to disk with picker or fallback
    await this.saveToDisk({
      getBlob: createZip,
      fileName: zipFileName,
      fileType: 'ZIP Archive',
      mimeType: 'application/zip',
      scopedLog,
      signal,
    });
  }

  // Private helper methods

  /**
   * Gets the total size of all files, sending concurrent HEAD requests only for files with no known size.
   * Concurrency is capped at HEAD_REQUEST_CONCURRENCY to avoid sending a burst of
   * hundreds of requests at once, which we suspect was triggering CDN rate limiting
   * and causing subsequent file download failures.
   * @returns Total size in bytes, or undefined if sizes cannot be determined
   */
  private async getTotalSize(
    files: Array<{ url: string; sizeBytes?: number }>,
    scopedLog: ScopedLogger,
    signal?: AbortSignal,
  ): Promise<number | undefined> {
    const startTime = performance.now();
    let headFailures = 0;
    try {
      let totalBytes = 0;
      for await (const size of mapConcurrentGenerator(
        files,
        async (file) => {
          if (typeof file.sizeBytes === 'number') {
            return file.sizeBytes;
          }
          try {
            const response = await fetch(file.url, { method: 'HEAD', signal });
            const contentLength = response.headers.get('Content-Length');
            return contentLength ? parseInt(contentLength, 10) : 0;
          } catch (error) {
            headFailures++;
            const err = error instanceof Error ? error : new Error(String(error));
            // If HEAD requests fail consistently it may indicate CDN rate limiting.
            // headFailures is logged below so we can correlate it with download failures.
            scopedLog.warn(`Could not get size for ${file.url}:`, err, {
              errorName: err.name,
              errorMessage: err.message,
            });
            return 0;
          }
        },
        HEAD_REQUEST_CONCURRENCY,
        signal,
      )) {
        totalBytes += size;
      }
      scopedLog.info('HEAD requests completed', {
        totalFiles: files.length,
        headFailures,
        durationMs: (performance.now() - startTime).toFixed(0),
        concurrency: HEAD_REQUEST_CONCURRENCY,
      });
      return totalBytes > 0 ? totalBytes : undefined;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      scopedLog.warn('Failed to calculate total size:', err, {
        errorName: err.name,
        errorMessage: err.message,
        headFailures,
        durationMs: (performance.now() - startTime).toFixed(0),
      });
      return undefined;
    }
  }

  /**
   * Orchestrates download + ZIP
   * @returns The final ZIP blob
   */
  private async createZipBlobFromFiles(
    files: FileWithPathInZip[],
    totalBytes: number | undefined,
    scopedLog: ScopedLogger,
    onProgress: DownloadProgressCallback | undefined,
    signal: AbortSignal | undefined,
  ): Promise<Blob> {
    throwIfAborted(signal);
    onProgress?.({ phase: 'preparing', percent: 0, totalBytes });
    const fileGenerator = this.createBatchDownloadsGenerator(files, scopedLog, signal);
    return this.consumeStreamToBlob(fileGenerator, totalBytes, onProgress, scopedLog, signal);
  }

  /**
   * Fetches one file and returns a file entry for the ZIP, or throws on failure/abort.
   */
  private async fetchOneFileForZip(
    file: FileWithPathInZip,
    scopedLog: ScopedLogger,
    signal: AbortSignal | undefined,
  ): Promise<ZipStreamEntry> {
    const result = await this.fetchSingleFile(file, scopedLog, signal);
    if (!result.success) {
      throwIfAborted(signal);
      scopedLog.error(`Failed to download file`, {
        path: result.path,
        url: result.url,
        errorName: result.errorName,
        errorMessage: result.errorMessage,
      });
      throw new Error(`Failed to download ${result.path}: ${result.errorMessage}`);
    }
    scopedLog.debug('File fetched', { path: result.path, sizeBytes: result.blob.size });
    return { name: result.path, input: result.blob };
  }

  /**
   * Creates a generator that downloads files with a sliding window of concurrency
   * and yields entries in completion order (so a slow file does not block others).
   */
  private async *createBatchDownloadsGenerator(
    files: FileWithPathInZip[],
    scopedLog: ScopedLogger,
    signal: AbortSignal | undefined,
  ): AsyncGenerator<ZipStreamEntry> {
    const totalFiles = files.length;
    scopedLog.debug(`Starting download of ${totalFiles} files for ZIP`);

    for await (const entry of mapConcurrentGenerator(
      files,
      (file) => this.fetchOneFileForZip(file, scopedLog, signal),
      DOWNLOAD_BATCH_SIZE,
      signal,
    )) {
      yield entry;
    }

    scopedLog.info('All files fetched', { totalFiles });
  }

  /**
   * Creates a throttled function that reports preparing progress based on current compressed size.
   */
  private createZipProgressReporter(
    totalBytes: number | undefined,
    onProgress: DownloadProgressCallback | undefined,
  ): DebouncedFunc<(compressedSize: number) => void> {
    return throttle(
      (compressedSize: number) => {
        if (!totalBytes || totalBytes <= 0) return;
        const percent = Math.min(99, Math.floor((compressedSize / totalBytes) * 100));
        onProgress?.({ phase: 'preparing', percent });
      },
      PROGRESS_REPORTS_THROTTLE_MS,
      { leading: true, trailing: false },
    );
  }

  /**
   * Reads the ZIP stream into blob parts, calling onProgress for each part.
   */
  private async readZipStreamToChunks(
    generator: AsyncGenerator<ZipStreamEntry>,
    signal: AbortSignal | undefined,
    scopedLog: ScopedLogger,
    reportProgress: (compressedSize: number) => void,
  ): Promise<BlobPart[]> {
    const zipStream = makeZip(generator);
    const reader = zipStream.getReader();

    scopedLog.info('ZIP generation started');
    try {
      const blobChunks: BlobPart[] = [];
      let compressedSize = 0;

      while (true) {
        throwIfAborted(signal);
        const { done, value } = await reader.read();
        if (done) break;
        blobChunks.push(value as BlobPart);
        compressedSize += value.length;
        reportProgress(compressedSize);
      }
      return blobChunks;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        scopedLog.info('Cancelling ZIP generation stream');
        await reader.cancel('Download cancelled');
        throw error;
      }
      const err = error instanceof Error ? error : new Error(String(error));
      scopedLog.error('Error during ZIP generation:', err, {
        errorName: err.name,
        errorMessage: err.message,
      });
      throw error;
    } finally {
      reader?.releaseLock();
    }
  }

  /**
   * Reads the ZIP stream into a Blob.
   * Reports a single preparing percentage (0-100) based on bytes read from the ZIP stream.
   */
  private async consumeStreamToBlob(
    filesGenerator: AsyncGenerator<ZipStreamEntry>,
    totalBytes: number | undefined,
    onProgress: DownloadProgressCallback | undefined,
    scopedLog: ScopedLogger,
    signal: AbortSignal | undefined,
  ): Promise<Blob> {
    const startTime = performance.now();
    const throttledProgress = this.createZipProgressReporter(totalBytes, onProgress);

    const blobParts = await this.readZipStreamToChunks(filesGenerator, signal, scopedLog, throttledProgress);
    throttledProgress.flush();
    const zipBlob = new Blob(blobParts, { type: 'application/zip' });
    const duration = performance.now() - startTime;
    onProgress?.({ phase: 'preparing', percent: 100 });
    scopedLog.info('ZIP generated', {
      sizeMB: (zipBlob.size / 1024 / 1024).toFixed(2),
      durationMs: duration.toFixed(0),
    });
    return zipBlob;
  }

  /**
   * Fetches a single file and returns a typed result.
   * Retries on TypeError (network-level failures) up to FETCH_MAX_RETRIES times.
   * TypeError indicates a network-level problem (connection reset, DNS failure, etc.)
   * which can be transient. HTTP errors (4xx/5xx) are not retried — they are
   * deterministic and a retry will not change the outcome.
   * @returns Success with blob or failure with error message
   */
  private async fetchSingleFile(
    file: FileWithPathInZip,
    scopedLog: ScopedLogger,
    signal?: AbortSignal,
  ): Promise<SingleFileFetchResult> {
    const { path, url } = file;
    let lastError: unknown;
    for (let attempt = 0; attempt <= FETCH_MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(url, { signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
        }
        const blob = await response.blob();
        if (attempt > 0) {
          scopedLog.info('File download succeeded after retry', { path, attempt });
        }
        return { success: true, path, url, blob };
      } catch (error) {
        lastError = error;
        const isNetworkError = error instanceof TypeError;
        if (attempt < FETCH_MAX_RETRIES && isNetworkError) {
          const err = error instanceof Error ? error : new Error(String(error));
          // Network errors (TypeError) can be transient — retry after a short delay.
          // If we see many retries in logs it suggests ongoing CDN / connectivity issues.
          scopedLog.warn('Network error fetching file, retrying', {
            path,
            attempt: attempt + 1,
            maxRetries: FETCH_MAX_RETRIES,
            retryDelayMs: FETCH_RETRY_DELAY_MS,
            errorName: err.name,
            errorMessage: err.message,
          });
          await new Promise((resolve) => setTimeout(resolve, FETCH_RETRY_DELAY_MS));
          continue;
        }
        break;
      }
    }
    return {
      success: false,
      path,
      url,
      errorMessage: lastError instanceof Error ? lastError.message : String(lastError),
      errorName: lastError instanceof Error ? lastError.name : 'Unknown',
    };
  }

  /**
   * Recursively collects all files from a folder structure with their paths
   */
  private collectFilesFromFolder(rootFolder: FolderEntry): FileWithPathInZip[] {
    const collectFiles = (items: (FileEntry | FolderEntry)[], currentPath = ''): FileWithPathInZip[] => {
      const files: FileWithPathInZip[] = [];

      for (const item of items) {
        if (this.isDownloadableFile(item)) {
          files.push({
            path: currentPath ? `${currentPath}/${item.name}` : item.name,
            url: item.url,
            sizeBytes: item.sizeBytes,
          });
        } else {
          // It's a folder, recurse into children
          const folderPath = currentPath ? `${currentPath}/${item.name}` : item.name;
          files.push(...collectFiles(item.children, folderPath));
        }
      }

      return files;
    };

    return collectFiles(rootFolder.children);
  }

  private isDownloadableFile(item: FileEntry | FolderEntry): item is FileEntry {
    return item.type === 'file';
  }

  private async downloadWithFallback(
    getBlob: () => Promise<Blob | undefined>,
    fileName: string,
    scopedLog: ScopedLogger,
    signal?: AbortSignal,
  ): Promise<void> {
    scopedLog.info('downloadWithFallback - Using getBlob for fallback download');
    const blob = await getBlob();
    throwIfAborted(signal);
    if (!blob) {
      const errorMessage = 'No blob found for file';
      scopedLog.error(errorMessage, fileName);
      throw new Error(errorMessage);
    }
    scopedLog.info('downloadWithFallback -Using blob for fallback download');

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    scopedLog.info('Download saved to disk', { saveMethod: 'fallback' });
  }

  private async saveToDisk({
    getBlob,
    fileName,
    fileType,
    mimeType,
    scopedLog,
    signal,
  }: {
    getBlob: () => Promise<Blob | undefined>;
    fileName: string;
    fileType: string;
    mimeType: string;
    scopedLog: ScopedLogger;
    signal?: AbortSignal;
  }): Promise<void> {
    if (!this.preferFilePicker || !('showSaveFilePicker' in window)) {
      await this.downloadWithFallback(getBlob, fileName, scopedLog, signal);
      return;
    }

    let blob: Blob | undefined;
    let blobGenerationComplete = false;

    scopedLog.info('Blob generation started');

    try {
      // 1. Generate blob FIRST (this is where ZIP generation happens)
      blob = await getBlob();
      throwIfAborted(signal);
      if (!blob) {
        throw new Error('No blob found for file');
      }
      blobGenerationComplete = true;
      scopedLog.info('Blob ready, showing save picker', { blobSizeBytes: blob.size });

      // 2. THEN show save picker
      const extension = getFileExtension(fileName, fileType);
      const fileExtension = extension ? `.${extension}` : '';
      const handle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [
          {
            description: fileType || 'File',
            // @ts-expect-error - File System Access API types are too restrictive for dynamic mime types
            accept: mimeType
              ? {
                  [mimeType]: [fileExtension || '*'],
                }
              : undefined,
          },
        ],
      });
      throwIfAborted(signal);
      // 3. Create writable and write immediately
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      scopedLog.info('Download saved to disk', { saveMethod: 'fileSystemAccess' });
    } catch (error) {
      throwIfAborted(signal);
      // Only swallow AbortError if it came from the save dialog (after blob generation)
      // If AbortError occurred during blob generation (download cancelled), re-throw it
      if (error instanceof Error && error.name === 'AbortError') {
        if (blobGenerationComplete) {
          // User cancelled the save dialog - this is not an error
          return;
        }
        // Download was cancelled by user - propagate the error
        throw error;
      }
      // For other errors, try fallback method with cached blob
      const err = error instanceof Error ? error : new Error(String(error));
      const logMessage = blobGenerationComplete
        ? 'Error using File System Access API:'
        : 'Error during download/blob generation:';
      scopedLog.warn(logMessage, err, {
        errorName: err.name,
        errorMessage: err.message,
      });

      // If we have the blob already, use it directly for fallback
      if (blob) {
        scopedLog.info('Using cached blob for fallback download');
        await this.downloadWithFallback(() => Promise.resolve(blob), fileName, scopedLog, signal);
      } else {
        scopedLog.info('Using getBlob for fallback download');
        // If blob generation failed, regenerate it for fallback
        await this.downloadWithFallback(getBlob, fileName, scopedLog, signal);
      }
    }
  }
}

// Singleton instance for convenience
export const browserDownloader = new BrowserDownloader();
